/**
 * Telemetry rules for authenticated content surfaces.
 * Session replay stays off. Advertising consent is denied on these routes.
 * Event properties keep an allowlist and drop screenplay, report, and title fields.
 */

const CONTENT_PREFIXES = [
  '/dashboard',
  '/screenplay',
  '/screenplays',
  '/upload',
  '/studio',
  '/projects',
  '/org',
  '/analysis',
  '/workspace',
  '/labs',
];

const ALLOWED_KEYS = new Set([
  'screenplayId',
  'analysisId',
  'format',
  'sizeKb',
  'durationMs',
  'budgetTier',
  'moduleName',
  'reportType',
  'taskType',
  'resultsCount',
  'companyId',
  'feature',
  'action',
  'renderMode',
  'includeAmbience',
  'includeFoley',
  'includeScore',
  'scoreSource',
  'sceneCount',
  'provider',
  'referrer_segment',
]);

export function isContentSurface(pathname: string): boolean {
  const path = pathname.split('?')[0]?.split('#')[0] ?? '';
  return CONTENT_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function pathnameOnly(href: string): string {
  try {
    return new URL(href, 'https://quilty.app').pathname;
  } catch {
    return href.split('?')[0]?.split('#')[0] ?? '';
  }
}

export function advertisingConsentForSurface(
  pathname: string,
  consented: boolean,
): 'granted' | 'denied' {
  if (isContentSurface(pathname)) return 'denied';
  return consented ? 'granted' : 'denied';
}

const SENSITIVE_KEY = /title|logline|dialogue|synopsis|screenplay_text|report_body|content|prompt|email|query/i;

export function sanitizeAnalyticsProperties(
  properties: Record<string, unknown> | undefined,
  pathname: string,
): Record<string, unknown> {
  const onContent = isContentSurface(pathname);
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties ?? {})) {
    if (SENSITIVE_KEY.test(key)) continue;
    if (onContent && !ALLOWED_KEYS.has(key)) continue;
    if (typeof value === 'string' && value.length > 200) continue;
    sanitized[key] = value;
  }
  return sanitized;
}
